import { Minus, Ok } from '@atom-learning/icons'
import * as RadixCheckbox from '@radix-ui/react-checkbox'
import * as React from 'react'

import { styled } from '~/styled'
import { overrideStyledVariantValue } from '~/utilities/override-styled-variant-value/overrideStyledVariantValue'

import { Icon } from '../icon/Icon'

const StyledCheckbox = styled(RadixCheckbox.Root, {
  base: [
    'appearance-none',
    'relative',
    'bg-transparent',
    'border',
    'border-grey-800',
    'rounded-[3px]',
    'text-white',
    'bg-white',
    'cursor-pointer',
    'size-4',
    'p-0',
    'flex',
    'items-center',
    'justify-center',
    'transition-all',
    'duration-50',
    'ease-out',
    'shrink-0',
    'not-disabled:before:-inset-2',
    'not-disabled:before:-z-1',
    'not-disabled:before:absolute',
    'not-disabled:before:bg-primary-300',
    "not-disabled:before:content-['']",
    'not-disabled:before:duration-125',
    'not-disabled:before:ease-in-out',
    'not-disabled:before:opacity-0',
    'not-disabled:before:rounded-[50%]',
    'not-disabled:before:scale-0',
    'not-disabled:before:transition-[opacity,scale]',
    'not-disabled:hover:before:opacity-100',
    'not-disabled:hover:before:scale-100',
    'data-[state=checked]:bg-primary-800',
    'data-[state=checked]:border-primary-800',
    'data-[state=indeterminate]:bg-primary-800',
    'data-[state=indeterminate]:border-primary-800',
    'focus:-outline-offset-1',
    'focus:outline-2',
    'focus:outline-primary-800',
    'focus:outline-solid',
    'disabled:cursor-not-allowed',
    'disabled:opacity-30'
  ],
  variants: {
    state: {
      error: ['border-danger']
    },
    size: {
      md: ['size-4'],
      lg: ['mt-px', 'size-6']
    }
  }
})

const toIconSize = {
  md: 'sm',
  lg: 'sm'
}

export const Checkbox = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof StyledCheckbox>
>(({ size = 'md', checked, ...rest }, ref) => {
  const iconSize = React.useMemo(
    () => overrideStyledVariantValue(size, (s) => toIconSize[s]),
    [size]
  )

  return (
    <div className="relative z-1">
      <StyledCheckbox ref={ref} checked={checked} size={size} {...rest}>
        <div className="absolute -inset-3 opacity-50" />
        <RadixCheckbox.Indicator asChild>
          <Icon
            is={checked === 'indeterminate' ? Minus : Ok}
            size={iconSize}
            className="stroke-3"
          />
        </RadixCheckbox.Indicator>
      </StyledCheckbox>
    </div>
  )
})

Checkbox.displayName = 'Checkbox'
